/**
 * AchievementPanel + ShareButtons — gamification + viral share UI.
 *
 * Why one file:
 *   These two components are tightly coupled — every badge has a
 *   `Share` action, and the share modal needs the badge context.
 *   Co-locating them avoids drilling 5 props through 3 components.
 *
 * Sharing strategy per platform:
 *   FB / X / LinkedIn — URL intent links (sharer.php / intent/tweet /
 *       shareArticle) opened in a popup window.
 *   Instagram — no URL share API; we copy a pre-formatted caption to
 *       clipboard AND offer a "Download PNG" so the user can post
 *       it as a Story.
 *   Copy Link — universal clipboard copy.
 *
 * The PNG generator paints a 1080×1080 canvas in the workspace's
 * brand color, embeds the badge emoji + name + brand_name + headline
 * stat. Suitable for IG Feed/Story and X/LinkedIn image post.
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Share2, Copy, CheckCircle, Download, X, Loader2,
  Facebook, Twitter, Linkedin, Instagram, ImageIcon,
} from "lucide-react";
import { API } from "../../config";

/* ------------------------------------------------------------------ */
/* AchievementPanel — grid of badge cards with progress               */
/* ------------------------------------------------------------------ */

export function AchievementPanel({ badges, workspaceId, accent }) {
  const [openShareFor, setOpenShareFor] = useState(null);
  if (!badges || badges.length === 0) return null;

  const earned = badges.filter((b) => b.earned);
  const earnedCount = earned.length;

  return (
    <section className="mb-8" data-testid="agent-os-achievements-panel">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-2" style={{ color: accent }}>
            Achievements · {earnedCount}/{badges.length}
          </div>
          <h2 className="text-2xl font-black">Tvoje úspechy</h2>
        </div>
        {earnedCount > 0 && (
          <span className="text-xs text-gray-500">Klikni na badge a zdieľaj 🚀</span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
        {badges.map((b) => (
          <BadgeCard
            key={b.id}
            badge={b}
            accent={accent}
            onShare={() => b.earned && setOpenShareFor(b)}
          />
        ))}
      </div>

      {openShareFor && (
        <ShareModal
          badge={openShareFor}
          workspaceId={workspaceId}
          accent={accent}
          onClose={() => setOpenShareFor(null)}
        />
      )}
    </section>
  );
}

function BadgeCard({ badge, accent, onShare }) {
  const tierGlow = badge.earned
    ? badge.tier === "gold"
      ? "shadow-[0_0_30px_rgba(250,204,21,0.25)]"
      : badge.tier === "silver"
      ? "shadow-[0_0_22px_rgba(203,213,225,0.18)]"
      : "shadow-[0_0_18px_rgba(180,83,9,0.20)]"
    : "";
  return (
    <button
      type="button"
      onClick={onShare}
      disabled={!badge.earned}
      data-testid={`agent-os-badge-${badge.id}`}
      className={`relative p-4 border-2 text-left transition-all ${tierGlow} ${
        badge.earned
          ? "border-gray-700 hover:border-white bg-black cursor-pointer hover:translate-y-[-2px]"
          : "border-gray-900 bg-black/40 opacity-50 cursor-default"
      }`}
      style={badge.earned ? { borderColor: accent + "60" } : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-3xl">{badge.emoji}</span>
        {badge.earned && (
          <Share2 className="w-4 h-4" style={{ color: accent }} data-testid={`agent-os-badge-${badge.id}-share-icon`} />
        )}
      </div>
      <h3 className="text-sm font-black mb-0.5">{badge.name}</h3>
      <p className="text-[10px] text-gray-500 leading-tight">{badge.desc}</p>
      {!badge.earned && (
        <div className="mt-2">
          <div className="h-1 bg-gray-900 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${badge.progress || 0}%`, backgroundColor: accent + "80" }} />
          </div>
          <div className="text-[9px] text-gray-600 mt-1 tabular-nums">
            {badge.current || 0}/{badge.threshold}
          </div>
        </div>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* ShareModal — create-share + platform buttons + PNG download        */
/* ------------------------------------------------------------------ */

function ShareModal({ badge, workspaceId, accent, onClose }) {
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  useState(() => null); // placeholder for lint
  // Create the share once on mount.
  if (loading && !share && !error) {
    axios.post(`${API}/agent-os/share/create`, {
      workspace_id: workspaceId, badge_id: badge.id,
      origin: typeof window !== "undefined" ? window.location.origin : undefined,
    })
      .then((r) => { setShare(r.data); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || "Share fail"); setLoading(false); });
  }

  const publicUrl = share?.public_url || "";
  const caption = `${badge.share_template_sk || badge.name} → ${publicUrl}\n\n#AgentOS #TopRankPulse`;

  const openPopup = (url, name) => {
    if (typeof window === "undefined") return;
    const w = 600, h = 600;
    const left = (window.screen.width / 2) - (w / 2);
    const top = (window.screen.height / 2) - (h / 2);
    window.open(url, name, `width=${w},height=${h},left=${left},top=${top}`);
  };

  const shareFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}&quote=${encodeURIComponent(badge.share_template_sk || "")}`;
    openPopup(u, "fb-share");
  };
  const shareTwitter = () => {
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(badge.share_template_sk || "")}&url=${encodeURIComponent(publicUrl)}`;
    openPopup(u, "tw-share");
  };
  const shareLinkedIn = () => {
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;
    openPopup(u, "ln-share");
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success("Caption skopírovaná 📋", { description: "Vlož do IG Story alebo Reels." }); }
    catch { toast.error("Skopíruj manuálne"); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link skopírovaný 📋"); }
    catch { toast.error("Skopíruj manuálne"); }
  };

  const downloadPng = async () => {
    if (!share) return;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = 1080; canvas.height = 1080;
    const ctx = canvas.getContext("2d");

    // Background — solid brand color
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, 1080, 1080);
    // Subtle darken band at the bottom for CTA contrast
    ctx.fillStyle = "rgba(0,0,0,0.20)";
    ctx.fillRect(0, 820, 1080, 260);

    // Emoji huge
    ctx.font = "320px system-ui, -apple-system, Segoe UI Emoji";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#000";
    ctx.fillText(badge.emoji, 540, 360);

    // Badge name
    ctx.font = "bold 84px system-ui, -apple-system";
    ctx.fillStyle = "#000";
    ctx.fillText(badge.name.toUpperCase(), 540, 620);

    // Subhead
    ctx.font = "32px system-ui, -apple-system";
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillText(badge.desc, 540, 700);

    // Footer band
    ctx.fillStyle = "#fff";
    ctx.font = "bold 28px system-ui";
    ctx.fillText("TopRankPulse · Agent OS", 540, 920);
    ctx.font = "24px system-ui";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fillText("toprankpulse.com/agent-os", 540, 980);

    // Trigger download
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-os-${badge.id}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PNG stiahnutý ⬇️", { description: "Vlož ho ako IG Story alebo Reel cover." });
    }, "image/png");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="agent-os-share-modal"
      onClick={onClose}
    >
      <div
        className="relative max-w-md w-full bg-[#0a0a0a] border-2 p-6"
        style={{ borderColor: accent + "60" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Zavri"
          data-testid="agent-os-share-modal-close"
          className="absolute top-3 right-3 text-gray-500 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {loading && (
          <div className="text-center py-12" data-testid="agent-os-share-modal-loading">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: accent }} />
            <p className="text-sm text-gray-400">Pripravujem share kartu…</p>
          </div>
        )}
        {error && (
          <div className="text-center py-12 text-rose-300 text-sm" data-testid="agent-os-share-modal-error">{error}</div>
        )}

        {share && !loading && (
          <>
            <div className="text-center mb-5">
              <div className="text-6xl mb-2">{badge.emoji}</div>
              <h2 className="text-2xl font-black mb-1">{badge.name}</h2>
              <p className="text-sm text-gray-400">{badge.share_template_sk}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <PlatformButton testid="share-fb"       label="Facebook"  icon={<Facebook  className="w-4 h-4" />} onClick={shareFacebook} accent={accent} />
              <PlatformButton testid="share-x"        label="X / Twitter" icon={<Twitter  className="w-4 h-4" />} onClick={shareTwitter}  accent={accent} />
              <PlatformButton testid="share-linkedin" label="LinkedIn"  icon={<Linkedin className="w-4 h-4" />} onClick={shareLinkedIn} accent={accent} />
              <PlatformButton testid="share-ig"       label="Instagram" icon={<Instagram className="w-4 h-4" />} onClick={copyCaption}  accent={accent} hint="Caption" />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <PlatformButton testid="share-png"  label="Download PNG"  icon={<Download className="w-4 h-4" />} onClick={downloadPng} accent={accent} />
              <PlatformButton testid="share-copy" label="Copy link"     icon={<Copy   className="w-4 h-4" />} onClick={copyLink}   accent={accent} />
            </div>

            <div className="p-3 bg-black border border-gray-800 text-[11px] text-gray-400 break-all" data-testid="agent-os-share-modal-url">
              {publicUrl}
            </div>

            <canvas ref={canvasRef} className="hidden" />
          </>
        )}
      </div>
    </div>
  );
}

function PlatformButton({ label, icon, onClick, accent, testid, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`agent-os-${testid}`}
      className="flex items-center justify-center gap-2 px-3 py-3 border border-gray-800 hover:border-white text-white text-xs font-bold uppercase tracking-widest transition-colors"
      style={{ borderColor: accent + "40" }}
    >
      {icon}
      <span>{label}</span>
      {hint && <span className="text-[9px] text-gray-500">({hint})</span>}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* SmallShareWinButton — compact share trigger for First Win tracker   */
/* ------------------------------------------------------------------ */

export function SmallShareWinButton({ workspaceId, accent, repliesGenerated }) {
  const [open, setOpen] = useState(false);
  if (!repliesGenerated || repliesGenerated < 1) return null;

  // Synthetic "badge" object representing the headline stat (not a
  // real catalog badge — just lets ShareModal reuse its UI).
  const syntheticBadge = {
    id: "first_win",  // map to the bronze first_win badge for snapshot
    name: "First Win",
    emoji: "🎯",
    desc: "Prvé reálne výsledky",
    share_template_sk: `🎯 Môj AI agent zvládol už ${repliesGenerated} ${repliesGenerated === 1 ? "zákazníka" : repliesGenerated < 5 ? "zákazníkov" : "zákazníkov"} bez mojej účasti.`,
    tier: "bronze",
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="agent-os-firstwin-share"
        className="inline-flex items-center gap-2 px-4 py-2 border-2 text-xs font-bold uppercase tracking-widest transition-colors hover:bg-black"
        style={{ borderColor: accent, color: accent }}
      >
        <Share2 className="w-3 h-3" /> Zdieľaj môj výsledok
      </button>
      {open && (
        <ShareModal
          badge={syntheticBadge}
          workspaceId={workspaceId}
          accent={accent}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
